import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { of } from 'rxjs';
import { BenefitPlan, BenefitEnrolment, BenefitClaim, BenefitDependent, BenefitPlanSummary, EligibleEmployee, EnrolmentConflict } from '../models/benefit.model';

// ---------------------------------------------------------------------------
// ID generators
// ---------------------------------------------------------------------------

let planIdSeq      = Date.now();
let claimIdSeq     = Date.now() + 1000;
let dependentIdSeq = Date.now() + 1500;

const nextPlanId      = () => `bp_${planIdSeq++}`;
const nextClaimId     = () => `bc_${claimIdSeq++}`;
const nextDependentId = () => `bd_${dependentIdSeq++}`;

// ---------------------------------------------------------------------------
// Seed data
// ---------------------------------------------------------------------------

let MOCK_PLANS: BenefitPlan[] = [
  // ── Insurance ────────────────────────────────────────────────────────────
  {
    id: 'bp_001',
    name: 'RWB Medical Gold 2026',
    category: 'INSURANCE',
    subType: 'Medical',
    legalEntityIds:        ['C001', 'C002'],
    legalEntityNames:      ['RWB CA', 'RWB International'],
    legalEntityCurrencies: ['GBP', 'GBP'],
    legalEntityLocales:    ['en-GB', 'en-GB'],
    status: 'ACTIVE',
    validityStart: '2026-01-01',
    validityEnd: '2026-12-31',
    enrolledCount: 142,
    maxCoverageAmount: 1500000,
    premiumPeriod: 'ANNUALLY',
    premiumAmount: 36000,
    employerContributionType: 'PERCENT',
    employerContributionAmount: 80,
    employeeContributionAmount: 7200,
    allowDependants: true,
    dependantConfig: [
      { relationshipType: 'Spouse',  allowed: true,  maxPerEmployee: 1, maxCoverageAmount: 750000, requiredDocuments: 'NIC + Marriage Certificate' },
      { relationshipType: 'Child',   allowed: true,  maxPerEmployee: 3, maxCoverageAmount: 500000, requiredDocuments: 'Birth Certificate' },
      { relationshipType: 'Parent',  allowed: false, maxPerEmployee: 2, maxCoverageAmount: 500000, requiredDocuments: 'NIC' },
      { relationshipType: 'Domestic Partner', allowed: false, maxPerEmployee: 1, maxCoverageAmount: null, requiredDocuments: '' }
    ],
    createdAt: '2025-12-01T08:00:00Z'
  },
  {
    id: 'bp_002',
    name: 'RWB Medical Standard 2026',
    category: 'INSURANCE',
    subType: 'Medical',
    legalEntityIds:        ['C003'],
    legalEntityNames:      ['Online Accounting'],
    legalEntityCurrencies: ['LKR'],
    legalEntityLocales:    ['en-LK'],
    status: 'ACTIVE',
    validityStart: '2026-01-01',
    validityEnd: '2026-12-31',
    enrolledCount: 88,
    maxCoverageAmount: 500000,
    premiumPeriod: 'ANNUALLY',
    premiumAmount: 18000,
    employerContributionType: 'PERCENT',
    employerContributionAmount: 100,
    employeeContributionAmount: 0,
    allowDependants: false,
    dependantConfig: [],
    createdAt: '2025-12-01T08:00:00Z'
  },
  {
    id: 'bp_003',
    name: 'Group Life Insurance 2026',
    category: 'INSURANCE',
    subType: 'Life',
    legalEntityIds:        ['C001', 'C002'],
    legalEntityNames:      ['RWB CA', 'RWB International'],
    legalEntityCurrencies: ['GBP', 'GBP'],
    legalEntityLocales:    ['en-GB', 'en-GB'],
    status: 'DRAFT',
    validityStart: '2026-07-01',
    validityEnd: '2027-06-30',
    enrolledCount: 0,
    maxCoverageAmount: 5000000,
    premiumPeriod: 'ANNUALLY',
    premiumAmount: 12000,
    employerContributionType: 'LKR',
    employerContributionAmount: 12000,
    employeeContributionAmount: 0,
    allowDependants: false,
    dependantConfig: [],
    createdAt: '2026-05-10T10:00:00Z'
  },
  {
    id: 'bp_004',
    name: 'Dental Care 2025',
    category: 'INSURANCE',
    subType: 'Dental',
    legalEntityIds:        ['C003'],
    legalEntityNames:      ['Online Accounting'],
    legalEntityCurrencies: ['LKR'],
    legalEntityLocales:    ['en-LK'],
    status: 'EXPIRED',
    validityStart: '2025-01-01',
    validityEnd: '2025-12-31',
    enrolledCount: 95,
    maxCoverageAmount: 100000,
    premiumPeriod: 'ANNUALLY',
    premiumAmount: 6000,
    employerContributionType: 'PERCENT',
    employerContributionAmount: 100,
    employeeContributionAmount: 0,
    allowDependants: false,
    dependantConfig: [],
    createdAt: '2024-12-15T08:00:00Z'
  },

  // ── Perk & Reimbursement ──────────────────────────────────────────────────
  {
    id: 'bp_007',
    name: 'Gym & Wellness 2026',
    category: 'PERK',
    subType: 'Gym',
    legalEntityIds:        ['C003', 'C004'],
    legalEntityNames:      ['Online Accounting', 'Online Solutions'],
    legalEntityCurrencies: ['LKR', 'LKR'],
    legalEntityLocales:    ['en-LK', 'en-LK'],
    status: 'ACTIVE',
    validityStart: '2026-01-01',
    validityEnd: '2026-12-31',
    enrolledCount: 74,
    benefitLimit: 60000,
    requireReceiptUpload: true,
    requireVendorName: true,
    requireExpenseDescription: true,
    requireExpenseDate: true,
    approvalWorkflow: 'MANAGER_PLUS_HR',
    createdAt: '2025-12-20T08:00:00Z'
  },
  {
    id: 'bp_008',
    name: 'Training & Certification 2026',
    category: 'PERK',
    subType: 'Training',
    legalEntityIds:        ['C003', 'C004'],
    legalEntityNames:      ['Online Accounting', 'Online Solutions'],
    legalEntityCurrencies: ['LKR', 'LKR'],
    legalEntityLocales:    ['en-LK', 'en-LK'],
    status: 'ACTIVE',
    validityStart: '2026-01-01',
    validityEnd: '2026-12-31',
    enrolledCount: 112,
    benefitLimit: 120000,
    requireReceiptUpload: true,
    requireVendorName: true,
    requireExpenseDescription: true,
    requireExpenseDate: true,
    approvalWorkflow: 'MANAGER_REVIEW',
    createdAt: '2025-12-20T08:00:00Z'
  },
  {
    id: 'bp_009',
    name: 'WFH Equipment 2025',
    category: 'PERK',
    subType: 'WFH Equipment',
    legalEntityIds:        ['C003'],
    legalEntityNames:      ['Online Accounting'],
    legalEntityCurrencies: ['LKR'],
    legalEntityLocales:    ['en-LK'],
    status: 'EXPIRED',
    validityStart: '2025-01-01',
    validityEnd: '2025-12-31',
    enrolledCount: 60,
    benefitLimit: 50000,
    requireReceiptUpload: true,
    requireVendorName: true,
    requireExpenseDescription: false,
    requireExpenseDate: true,
    approvalWorkflow: 'MANAGER_REVIEW',
    createdAt: '2024-12-15T08:00:00Z'
  },
  {
    id: 'bp_010',
    name: 'EAP Programme 2026',
    category: 'PERK',
    subType: 'EAP',
    legalEntityIds:        ['C001'],
    legalEntityNames:      ['RWB CA'],
    legalEntityCurrencies: ['GBP'],
    legalEntityLocales:    ['en-GB'],
    status: 'DRAFT',
    validityStart: '2026-08-01',
    validityEnd: '2027-07-31',
    enrolledCount: 0,
    benefitLimit: 30000,
    requireReceiptUpload: false,
    requireVendorName: true,
    requireExpenseDescription: true,
    requireExpenseDate: false,
    approvalWorkflow: 'AUTO',
    createdAt: '2026-06-10T08:00:00Z'
  }
];

let MOCK_ENROLMENTS: BenefitEnrolment[] = [
  // ── RWB Medical Gold 2026 ─────────────────────────────────────────────────
  { id: 'be_001', planId: 'bp_001', planName: 'RWB Medical Gold 2026',         category: 'INSURANCE', subType: 'Medical',   employeeId: 'emp-123', employeeName: 'Dilshan Rajapaksha',                employeeCode: 'E0123', company: 'RWB CA',            department: 'Engineering', effectiveFrom: '2026-01-01', effectiveTo: '2026-12-31', status: 'ACTIVE',    createdAt: '2026-01-01T08:00:00Z' },
  { id: 'be_002', planId: 'bp_001', planName: 'RWB Medical Gold 2026',         category: 'INSURANCE', subType: 'Medical',   employeeId: 'emp-456', employeeName: 'Amali Perera',                      employeeCode: 'E0456', company: 'RWB CA',            department: 'Engineering', effectiveFrom: '2026-01-01', effectiveTo: '2026-12-31', status: 'ACTIVE',    createdAt: '2026-01-01T08:00:00Z' },
  { id: 'be_003', planId: 'bp_001', planName: 'RWB Medical Gold 2026',         category: 'INSURANCE', subType: 'Medical',   employeeId: 'emp-201', employeeName: 'Arjun Peiris',                      employeeCode: 'E0201', company: 'RWB CA',            department: 'Engineering', effectiveFrom: '2026-01-01', effectiveTo: '2026-12-31', status: 'ACTIVE',    createdAt: '2026-01-01T08:00:00Z' },
  { id: 'be_004', planId: 'bp_001', planName: 'RWB Medical Gold 2026',         category: 'INSURANCE', subType: 'Medical',   employeeId: 'emp-202', employeeName: 'Thilini Fernando',                  employeeCode: 'E0202', company: 'RWB CA',            department: 'HR',          effectiveFrom: '2026-01-01', effectiveTo: '2026-12-31', status: 'ACTIVE',    createdAt: '2026-01-01T08:00:00Z' },
  { id: 'be_005', planId: 'bp_001', planName: 'RWB Medical Gold 2026',         category: 'INSURANCE', subType: 'Medical',   employeeId: 'emp-321', employeeName: 'Kavindi Jayasuriya',                employeeCode: 'E0321', company: 'RWB CA',            department: 'HR',          effectiveFrom: '2026-01-01', effectiveTo: '2026-12-31', status: 'PENDING',   createdAt: '2026-06-10T09:00:00Z' },
  // ── RWB Medical Standard 2026 ─────────────────────────────────────────────
  { id: 'be_006', planId: 'bp_002', planName: 'RWB Medical Standard 2026',     category: 'INSURANCE', subType: 'Medical',   employeeId: 'emp-204', employeeName: 'Sachini Gunasekara',                employeeCode: 'E0204', company: 'RWB International', department: 'Marketing',   effectiveFrom: '2026-01-01', effectiveTo: '2026-12-31', status: 'ACTIVE',    createdAt: '2026-01-01T08:00:00Z' },
  { id: 'be_007', planId: 'bp_002', planName: 'RWB Medical Standard 2026',     category: 'INSURANCE', subType: 'Medical',   employeeId: 'emp-205', employeeName: 'Dulitha Rathnayake',                employeeCode: 'E0205', company: 'RWB International', department: 'Operations',  effectiveFrom: '2026-01-01', effectiveTo: '2026-12-31', status: 'ACTIVE',    createdAt: '2026-01-01T08:00:00Z' },
  { id: 'be_008', planId: 'bp_002', planName: 'RWB Medical Standard 2026',     category: 'INSURANCE', subType: 'Medical',   employeeId: 'emp-789', employeeName: 'Dinesh Silva',                      employeeCode: 'E0789', company: 'RWB International', department: 'Product',     effectiveFrom: '2026-01-01', effectiveTo: '2026-12-31', status: 'CANCELLED', createdAt: '2026-01-01T08:00:00Z' },
  // ── Dental Care 2025 (expired) ────────────────────────────────────────────
  { id: 'be_009', planId: 'bp_004', planName: 'Dental Care 2025',              category: 'INSURANCE', subType: 'Dental',    employeeId: 'emp-321', employeeName: 'Kavindi Jayasuriya',                employeeCode: 'E0321', company: 'RWB CA',            department: 'HR',          effectiveFrom: '2025-01-01', effectiveTo: '2025-12-31', status: 'EXPIRED',   createdAt: '2025-01-01T08:00:00Z' },
  { id: 'be_010', planId: 'bp_004', planName: 'Dental Care 2025',              category: 'INSURANCE', subType: 'Dental',    employeeId: 'emp-202', employeeName: 'Thilini Fernando',                  employeeCode: 'E0202', company: 'RWB CA',            department: 'HR',          effectiveFrom: '2025-01-01', effectiveTo: '2025-12-31', status: 'EXPIRED',   createdAt: '2025-01-01T08:00:00Z' },
  // ── Gym & Wellness 2026 ───────────────────────────────────────────────────
  { id: 'be_011', planId: 'bp_007', planName: 'Gym & Wellness 2026',           category: 'PERK',      subType: 'Gym',       employeeId: 'emp-123', employeeName: 'Dilshan Rajapaksha',                employeeCode: 'E0123', company: 'RWB CA',            department: 'Engineering', effectiveFrom: '2026-01-01', effectiveTo: '2026-12-31', status: 'ACTIVE',    createdAt: '2026-01-01T08:00:00Z' },
  { id: 'be_012', planId: 'bp_007', planName: 'Gym & Wellness 2026',           category: 'PERK',      subType: 'Gym',       employeeId: 'emp-456', employeeName: 'Amali Perera',                      employeeCode: 'E0456', company: 'RWB CA',            department: 'Engineering', effectiveFrom: '2026-01-01', effectiveTo: '2026-12-31', status: 'ACTIVE',    createdAt: '2026-01-01T08:00:00Z' },
  { id: 'be_013', planId: 'bp_007', planName: 'Gym & Wellness 2026',           category: 'PERK',      subType: 'Gym',       employeeId: 'emp-204', employeeName: 'Sachini Gunasekara',                employeeCode: 'E0204', company: 'RWB International', department: 'Marketing',   effectiveFrom: '2026-01-01', effectiveTo: '2026-12-31', status: 'ACTIVE',    createdAt: '2026-01-01T08:00:00Z' },
  { id: 'be_014', planId: 'bp_007', planName: 'Gym & Wellness 2026',           category: 'PERK',      subType: 'Gym',       employeeId: 'emp-321', employeeName: 'Kavindi Jayasuriya',                employeeCode: 'E0321', company: 'RWB CA',            department: 'HR',          effectiveFrom: '2026-01-01', effectiveTo: '2026-12-31', status: 'ACTIVE',    createdAt: '2026-01-01T08:00:00Z' },
  { id: 'be_015', planId: 'bp_007', planName: 'Gym & Wellness 2026',           category: 'PERK',      subType: 'Gym',       employeeId: 'emp-205', employeeName: 'Dulitha Rathnayake',                employeeCode: 'E0205', company: 'RWB International', department: 'Operations',  effectiveFrom: '2026-01-01', effectiveTo: '2026-12-31', status: 'ACTIVE',    createdAt: '2026-01-01T08:00:00Z' },
  // ── Training & Certification 2026 ─────────────────────────────────────────
  { id: 'be_016', planId: 'bp_008', planName: 'Training & Certification 2026', category: 'PERK',      subType: 'Training',  employeeId: 'emp-123', employeeName: 'Dilshan Rajapaksha',                employeeCode: 'E0123', company: 'RWB CA',            department: 'Engineering', effectiveFrom: '2026-01-01', effectiveTo: '2026-12-31', status: 'PENDING',   createdAt: '2026-06-10T09:00:00Z' },
  { id: 'be_017', planId: 'bp_008', planName: 'Training & Certification 2026', category: 'PERK',      subType: 'Training',  employeeId: 'emp-201', employeeName: 'Arjun Peiris',                      employeeCode: 'E0201', company: 'RWB CA',            department: 'Engineering', effectiveFrom: '2026-01-01', effectiveTo: '2026-12-31', status: 'ACTIVE',    createdAt: '2026-01-01T08:00:00Z' },
  { id: 'be_018', planId: 'bp_008', planName: 'Training & Certification 2026', category: 'PERK',      subType: 'Training',  employeeId: 'emp-789', employeeName: 'Dinesh Silva',                      employeeCode: 'E0789', company: 'RWB International', department: 'Product',     effectiveFrom: '2026-01-01', effectiveTo: '2026-12-31', status: 'PENDING',   createdAt: '2026-06-10T09:00:00Z' },
  { id: 'be_019', planId: 'bp_008', planName: 'Training & Certification 2026', category: 'PERK',      subType: 'Training',  employeeId: 'emp-301', employeeName: 'Tharindu Wickramasinghe Bandara',   employeeCode: 'E0301', company: 'RWB CA',            department: 'Engineering', effectiveFrom: '2026-01-01', effectiveTo: '2026-12-31', status: 'ACTIVE',    createdAt: '2026-01-01T08:00:00Z' },
  { id: 'be_020', planId: 'bp_008', planName: 'Training & Certification 2026', category: 'PERK',      subType: 'Training',  employeeId: 'emp-412', employeeName: 'Supun Wickramarachchi Abeyasinghe', employeeCode: 'E0412', company: 'RWB International', department: 'Engineering', effectiveFrom: '2026-01-01', effectiveTo: '2026-12-31', status: 'ACTIVE',    createdAt: '2026-01-01T08:00:00Z' },
];

let MOCK_CLAIMS: BenefitClaim[] = [
  {
    id: 'bc_001',
    planId: 'bp_007',
    planName: 'Gym & Wellness 2026',
    planCategory: 'PERK',
    employeeId: 'emp-456',
    employeeName: 'Amali Perera',
    employeeDepartment: 'Engineering',
    description: 'Monthly gym membership — FitZone Colombo',
    amount: 4500,
    expenseDate: '2026-06-05',
    vendorName: 'FitZone Health Club',
    documentCount: 2,
    status: 'PENDING_HR',
    submittedAt: '2026-06-10T09:00:00Z',
    documents: [
      { name: 'gym_membership_june_2026.pdf', url: '/assets/mock-docs/bc_001_receipt.pdf' },
      { name: 'membership_card_scan.jpg',     url: '/assets/mock-docs/bc_001_card.jpg' }
    ]
  },
  {
    id: 'bc_002',
    planId: 'bp_008',
    planName: 'Training & Certification 2026',
    planCategory: 'PERK',
    employeeId: 'emp-789',
    employeeName: 'Dinesh Silva',
    employeeDepartment: 'Product',
    description: 'AWS Solutions Architect certification exam fee',
    amount: 32000,
    expenseDate: '2026-05-28',
    vendorName: 'Amazon Web Services',
    documentCount: 1,
    status: 'PENDING_MANAGER',
    submittedAt: '2026-05-30T14:00:00Z',
    documents: [
      { name: 'aws_exam_invoice.pdf', url: '/assets/mock-docs/bc_002_invoice.pdf' }
    ]
  },
  {
    id: 'bc_003',
    planId: 'bp_007',
    planName: 'Gym & Wellness 2026',
    planCategory: 'PERK',
    employeeId: 'emp-321',
    employeeName: 'Kavindi Jayasuriya',
    employeeDepartment: 'HR',
    description: 'Yoga studio — June session',
    amount: 3800,
    expenseDate: '2026-06-01',
    vendorName: 'Serenity Yoga Studio',
    documentCount: 1,
    status: 'APPROVED',
    submittedAt: '2026-06-03T11:00:00Z',
    approverNote: 'Approved as part of Wellness Week initiative.',
    documents: [
      { name: 'yoga_studio_receipt_june.pdf', url: '/assets/mock-docs/bc_003_receipt.pdf' }
    ]
  },
  {
    id: 'bc_004',
    planId: 'bp_008',
    planName: 'Training & Certification 2026',
    planCategory: 'PERK',
    employeeId: 'emp-789',
    employeeName: 'Dinesh Silva',
    employeeDepartment: 'Product',
    description: 'Google UX Design Certificate — Coursera',
    amount: 15000,
    expenseDate: '2026-06-12',
    vendorName: 'Coursera',
    documentCount: 1,
    status: 'PENDING_HR',
    submittedAt: '2026-06-14T10:30:00Z',
    documents: [
      { name: 'coursera_payment_receipt.pdf', url: '/assets/mock-docs/bc_004_receipt.pdf' }
    ]
  },
  {
    id: 'bc_005',
    planId: 'bp_007',
    planName: 'Gym & Wellness 2026',
    planCategory: 'PERK',
    employeeId: 'emp-201',
    employeeName: 'Arjun Peiris',
    employeeDepartment: 'Engineering',
    description: "Monthly gym membership — Gold's Gym Colombo 3",
    amount: 5200,
    expenseDate: '2026-06-10',
    vendorName: "Gold's Gym",
    documentCount: 2,
    status: 'PENDING_HR',
    submittedAt: '2026-06-13T08:45:00Z',
    documents: [
      { name: 'golds_gym_receipt_june.pdf', url: '/assets/mock-docs/bc_005_receipt.pdf' },
      { name: 'membership_certificate.pdf', url: '/assets/mock-docs/bc_005_cert.pdf' }
    ]
  },
  {
    id: 'bc_006',
    planId: 'bp_008',
    planName: 'Training & Certification 2026',
    planCategory: 'PERK',
    employeeId: 'emp-301',
    employeeName: 'Tharindu Wickramasinghe Bandara',
    employeeDepartment: 'Engineering',
    description: 'Kubernetes Administrator (CKA) exam fee',
    amount: 42000,
    expenseDate: '2026-05-15',
    vendorName: 'The Linux Foundation',
    documentCount: 2,
    status: 'APPROVED',
    submittedAt: '2026-05-16T09:00:00Z',
    approverNote: 'Approved — directly relevant to infrastructure roadmap.',
    documents: [
      { name: 'cka_exam_invoice.pdf',      url: '/assets/mock-docs/bc_006_invoice.pdf' },
      { name: 'cka_pass_certificate.pdf',  url: '/assets/mock-docs/bc_006_cert.pdf' }
    ]
  },
  {
    id: 'bc_007',
    planId: 'bp_007',
    planName: 'Gym & Wellness 2026',
    planCategory: 'PERK',
    employeeId: 'emp-204',
    employeeName: 'Sachini Gunasekara',
    employeeDepartment: 'Marketing',
    description: 'Spa & wellness day package',
    amount: 8900,
    expenseDate: '2026-06-07',
    vendorName: 'Jetwing Spa Colombo',
    documentCount: 1,
    status: 'REJECTED',
    submittedAt: '2026-06-08T13:00:00Z',
    approverNote: 'Spa services are not covered under this plan. Please refer to the plan eligibility criteria.',
    documents: [
      { name: 'jetwing_spa_receipt.pdf', url: '/assets/mock-docs/bc_007_receipt.pdf' }
    ]
  },
  {
    id: 'bc_008',
    planId: 'bp_007',
    planName: 'Gym & Wellness 2026',
    planCategory: 'PERK',
    employeeId: 'emp-205',
    employeeName: 'Dulitha Rathnayake',
    employeeDepartment: 'Operations',
    description: 'Swimming club monthly membership',
    amount: 3200,
    expenseDate: '2026-06-01',
    vendorName: 'Aquatic Sports Club',
    documentCount: 1,
    status: 'PENDING_HR',
    submittedAt: '2026-06-11T15:20:00Z',
    documents: [
      { name: 'aquatic_club_receipt_june.pdf', url: '/assets/mock-docs/bc_008_receipt.pdf' }
    ]
  },
  {
    id: 'bc_009',
    planId: 'bp_008',
    planName: 'Training & Certification 2026',
    planCategory: 'PERK',
    employeeId: 'emp-412',
    employeeName: 'Supun Wickramarachchi Abeyasinghe',
    employeeDepartment: 'Engineering',
    description: 'AWS Cloud Practitioner certification renewal',
    amount: 18500,
    expenseDate: '2026-06-09',
    vendorName: 'Amazon Web Services',
    documentCount: 1,
    status: 'PENDING_MANAGER',
    submittedAt: '2026-06-10T11:00:00Z',
    documents: [
      { name: 'aws_cloud_practitioner_invoice.pdf', url: '/assets/mock-docs/bc_009_invoice.pdf' }
    ]
  },
  {
    id: 'bc_010',
    planId: 'bp_007',
    planName: 'Gym & Wellness 2026',
    planCategory: 'PERK',
    employeeId: 'emp-321',
    employeeName: 'Kavindi Jayasuriya',
    employeeDepartment: 'HR',
    description: 'Pilates studio — May session',
    amount: 4100,
    expenseDate: '2026-05-28',
    vendorName: 'Studio Pilates',
    documentCount: 2,
    status: 'APPROVED',
    submittedAt: '2026-05-29T10:00:00Z',
    approverNote: null,
    documents: [
      { name: 'studio_pilates_receipt_may.pdf', url: '/assets/mock-docs/bc_010_receipt1.pdf' },
      { name: 'class_schedule_proof.pdf',       url: '/assets/mock-docs/bc_010_receipt2.pdf' }
    ]
  },
  // ── Current user (emp-123 — Dilshan Rajapaksha) claims ───────────────────────
  {
    id: 'bc_011',
    planId: 'bp_007',
    planName: 'Gym & Wellness 2026',
    planCategory: 'PERK',
    employeeId: 'emp-123',
    employeeName: 'Dilshan Rajapaksha',
    employeeDepartment: 'Engineering',
    description: 'Monthly gym membership — June',
    amount: 4800,
    expenseDate: '2026-06-15',
    vendorName: 'FitZone Health Club',
    documentCount: 1,
    status: 'PENDING_HR',
    submittedAt: '2026-06-16T09:30:00Z',
    documents: [
      { name: 'fitzone_receipt_june.pdf', url: '/assets/mock-docs/bc_011_receipt.pdf' }
    ]
  },
  {
    id: 'bc_012',
    planId: 'bp_007',
    planName: 'Gym & Wellness 2026',
    planCategory: 'PERK',
    employeeId: 'emp-123',
    employeeName: 'Dilshan Rajapaksha',
    employeeDepartment: 'Engineering',
    description: 'Sports gear — running shoes',
    amount: 6200,
    expenseDate: '2026-06-08',
    vendorName: 'Sportsline Colombo',
    documentCount: 1,
    status: 'PENDING_MANAGER',
    submittedAt: '2026-06-09T14:15:00Z',
    documents: [
      { name: 'sportsline_invoice.pdf', url: '/assets/mock-docs/bc_012_invoice.pdf' }
    ]
  },
  {
    id: 'bc_013',
    planId: 'bp_007',
    planName: 'Gym & Wellness 2026',
    planCategory: 'PERK',
    employeeId: 'emp-123',
    employeeName: 'Dilshan Rajapaksha',
    employeeDepartment: 'Engineering',
    description: 'Monthly gym membership — May',
    amount: 4800,
    expenseDate: '2026-05-12',
    vendorName: 'FitZone Health Club',
    documentCount: 1,
    status: 'APPROVED',
    submittedAt: '2026-05-13T10:00:00Z',
    approverNote: 'Approved — within monthly limit.',
    documents: [
      { name: 'fitzone_receipt_may.pdf', url: '/assets/mock-docs/bc_013_receipt.pdf' }
    ]
  },
  {
    id: 'bc_014',
    planId: 'bp_007',
    planName: 'Gym & Wellness 2026',
    planCategory: 'PERK',
    employeeId: 'emp-123',
    employeeName: 'Dilshan Rajapaksha',
    employeeDepartment: 'Engineering',
    description: 'Protein supplements',
    amount: 9500,
    expenseDate: '2026-05-20',
    vendorName: 'NutriMart',
    documentCount: 1,
    status: 'REJECTED',
    submittedAt: '2026-05-21T11:40:00Z',
    approverNote: 'Supplements are not covered under the Gym & Wellness plan.',
    documents: [
      { name: 'nutrimart_receipt.pdf', url: '/assets/mock-docs/bc_014_receipt.pdf' }
    ]
  },
  {
    id: 'bc_015',
    planId: 'bp_007',
    planName: 'Gym & Wellness 2026',
    planCategory: 'PERK',
    employeeId: 'emp-123',
    employeeName: 'Dilshan Rajapaksha',
    employeeDepartment: 'Engineering',
    description: 'Gym membership — duplicate submission',
    amount: 4800,
    expenseDate: '2026-04-10',
    vendorName: 'FitZone Health Club',
    documentCount: 1,
    status: 'CANCELLED',
    submittedAt: '2026-04-11T08:20:00Z',
    documents: [
      { name: 'fitzone_receipt_april.pdf', url: '/assets/mock-docs/bc_015_receipt.pdf' }
    ]
  }
];

let MOCK_DEPENDENTS: BenefitDependent[] = [
  {
    id: 'bd_001',
    employeeId: 'emp-123',
    fullName: 'Sanduni Rajapaksha',
    relationship: 'Spouse',
    nicPassport: '198876543210',
    dateOfBirth: '1988-03-12',
    gender: 'FEMALE',
    verificationStatus: 'VERIFIED',
    coveredPlanIds: ['bp_001'],
    submittedDocuments: ['NIC Copy', 'Marriage Certificate'],
    createdAt: '2026-01-15T08:00:00Z'
  },
  {
    id: 'bd_002',
    employeeId: 'emp-123',
    fullName: 'Nishan Rajapaksha',
    relationship: 'Child',
    nicPassport: '2015-N-1234',
    dateOfBirth: '2015-07-22',
    gender: 'MALE',
    verificationStatus: 'PENDING',
    coveredPlanIds: [],
    submittedDocuments: ['Birth Certificate'],
    createdAt: '2026-06-12T10:00:00Z'
  }
];

const MOCK_SUMMARY: BenefitPlanSummary = {
  activeCount: 3,
  pendingCount: 1,
  monthlyAllowanceLkr: 5000,
  claimBalanceLkr: 52500
};

// ---------------------------------------------------------------------------
// Eligible employees (Slice 3 — Bulk Enrolment)
// ---------------------------------------------------------------------------

const MOCK_ALL_ELIGIBLE: EligibleEmployee[] = [
  // ── RWB CA — Engineering ───────────────────────────────────────────────────
  { id: 'emp-201', name: 'Arjun Peiris',                         employeeCode: 'E0201', company: 'RWB CA',            department: 'Engineering',  line: 'Backend Team',         position: 'Software Engineer' },
  { id: 'emp-123', name: 'Dilshan Rajapaksha',                   employeeCode: 'E0123', company: 'RWB CA',            department: 'Engineering',  line: 'Frontend Team',        position: 'Software Engineer' },
  { id: 'emp-456', name: 'Amali Perera',                         employeeCode: 'E0456', company: 'RWB CA',            department: 'Engineering',  line: 'Frontend Team',        position: 'Software Engineer' },
  { id: 'emp-206', name: 'Hasitha Madushan',                     employeeCode: 'E0206', company: 'RWB CA',            department: 'Engineering',  line: 'DevOps',               position: 'IT Assistant' },
  { id: 'emp-301', name: 'Tharindu Wickramasinghe Bandara',      employeeCode: 'E0301', company: 'RWB CA',            department: 'Engineering',  line: 'Backend Team',         position: 'Software Engineer' },
  { id: 'emp-302', name: 'Nadeesha Kumari Samaraweera',          employeeCode: 'E0302', company: 'RWB CA',            department: 'Engineering',  line: 'QA & Testing',         position: 'IT Assistant' },
  { id: 'emp-303', name: 'Ruwan Bandara',                        employeeCode: 'E0303', company: 'RWB CA',            department: 'Engineering',  line: 'Mobile Team',          position: 'Software Engineer' },
  // ── RWB CA — HR ───────────────────────────────────────────────────────────
  { id: 'emp-202', name: 'Thilini Fernando',                     employeeCode: 'E0202', company: 'RWB CA',            department: 'HR',           line: 'HR Operations',        position: 'HR Assistant' },
  { id: 'emp-321', name: 'Kavindi Jayasuriya',                   employeeCode: 'E0321', company: 'RWB CA',            department: 'HR',           line: 'HR Operations',        position: 'HR Assistant' },
  { id: 'emp-304', name: 'Priyanka Dissanayake Karunarathna',    employeeCode: 'E0304', company: 'RWB CA',            department: 'HR',           line: 'Talent Acquisition',   position: 'HR Assistant' },
  // ── RWB CA — Finance ──────────────────────────────────────────────────────
  { id: 'emp-203', name: 'Nuwan Wijesinghe',                     employeeCode: 'E0203', company: 'RWB CA',            department: 'Finance',      line: 'Accounts',             position: 'Accounts Executive' },
  { id: 'emp-305', name: 'Chamari Senaratne',                    employeeCode: 'E0305', company: 'RWB CA',            department: 'Finance',      line: 'Payroll',              position: 'Accounts Executive' },
  // ── RWB CA — Product ──────────────────────────────────────────────────────
  { id: 'emp-207', name: 'Rasika Dissanayake',                   employeeCode: 'E0207', company: 'RWB CA',            department: 'Product',      line: 'Product Strategy',     position: 'Marketing Manager' },
  { id: 'emp-306', name: 'Isuru Pathirana',                      employeeCode: 'E0306', company: 'RWB CA',            department: 'Product',      line: 'Product Design',       position: 'IT Assistant' },
  // ── RWB CA — Marketing ────────────────────────────────────────────────────
  { id: 'emp-307', name: 'Shanika Ratnayake',                    employeeCode: 'E0307', company: 'RWB CA',            department: 'Marketing',    line: 'Content',              position: 'Marketing Executive' },
  { id: 'emp-308', name: 'Buddhika Siriwardena',                 employeeCode: 'E0308', company: 'RWB CA',            department: 'Marketing',    line: 'Brand',                position: 'Marketing Manager' },
  // ── RWB International — Engineering ───────────────────────────────────────
  { id: 'emp-401', name: 'Waruni Amarasinghe',                   employeeCode: 'E0401', company: 'RWB International', department: 'Engineering',  line: 'Mobile Team',          position: 'Software Engineer' },
  { id: 'emp-402', name: 'Samantha Rajapaksha Gunawardena',      employeeCode: 'E0402', company: 'RWB International', department: 'Engineering',  line: 'Mobile Team',          position: 'IT Assistant' },
  { id: 'emp-403', name: 'Lasith Herath Bandara',                employeeCode: 'E0403', company: 'RWB International', department: 'Engineering',  line: 'Backend Team',         position: 'Software Engineer' },
  { id: 'emp-412', name: 'Supun Wickramarachchi Abeyasinghe',    employeeCode: 'E0412', company: 'RWB International', department: 'Engineering',  line: 'DevOps',               position: 'IT Assistant' },
  // ── RWB International — Marketing ─────────────────────────────────────────
  { id: 'emp-204', name: 'Sachini Gunasekara',                   employeeCode: 'E0204', company: 'RWB International', department: 'Marketing',    line: 'Digital Marketing',    position: 'Marketing Executive' },
  { id: 'emp-407', name: 'Pasindu Jayawardena',                  employeeCode: 'E0407', company: 'RWB International', department: 'Marketing',    line: 'Performance Marketing', position: 'Marketing Executive' },
  { id: 'emp-408', name: 'Roshani Chandrasekara',                employeeCode: 'E0408', company: 'RWB International', department: 'Marketing',    line: 'Performance Marketing', position: 'Marketing Manager' },
  // ── RWB International — Operations ────────────────────────────────────────
  { id: 'emp-205', name: 'Dulitha Rathnayake',                   employeeCode: 'E0205', company: 'RWB International', department: 'Operations',   line: 'Logistics',            position: 'Sales Coordinator' },
  { id: 'emp-405', name: 'Sithumi Madushanka',                   employeeCode: 'E0405', company: 'RWB International', department: 'Operations',   line: 'Supply Chain',         position: 'Sales Coordinator' },
  { id: 'emp-406', name: 'Dhanuka Senanayake Abeyrathne',        employeeCode: 'E0406', company: 'RWB International', department: 'Operations',   line: 'Supply Chain',         position: 'Sales Coordinator' },
  // ── RWB International — HR ────────────────────────────────────────────────
  { id: 'emp-409', name: 'Mihiri Wickremasinghe',                employeeCode: 'E0409', company: 'RWB International', department: 'HR',           line: 'Talent',               position: 'HR Assistant' },
  // ── RWB International — Finance ───────────────────────────────────────────
  { id: 'emp-410', name: 'Chathuri Nanayakkara',                 employeeCode: 'E0410', company: 'RWB International', department: 'Finance',      line: 'Accounts',             position: 'Accounts Executive' },
  { id: 'emp-411', name: 'Anuradha Karunarathna',                employeeCode: 'E0411', company: 'RWB International', department: 'Finance',      line: 'Payroll',              position: 'Accounts Executive' },
  // ── RWB International — Product ───────────────────────────────────────────
  { id: 'emp-789', name: 'Dinesh Silva',                         employeeCode: 'E0789', company: 'RWB International', department: 'Product',      line: 'UX Research',          position: 'Marketing Executive' },
  { id: 'emp-413', name: 'Thilanka Rajapaksha',                  employeeCode: 'E0413', company: 'RWB International', department: 'Product',      line: 'Product Strategy',     position: 'Marketing Manager' },
];

// ---------------------------------------------------------------------------
// Interceptor
// ---------------------------------------------------------------------------

export const mockBenefitApiInterceptor: HttpInterceptorFn = (req, next) => {
  const url = req.url;

  if (!url.includes('/benefit/')) {
    return next(req);
  }

  const ok = (body: unknown) =>
    of(new HttpResponse({ status: 200, body }));

  // ── Plans ─────────────────────────────────────────────────────────────────

  if (req.method === 'GET' && url.includes('/benefit/plans') && !url.split('/benefit/plans')[1]?.match(/^\/[^/]+\//)) {
    const rawSuffix = url.split('/benefit/plans')[1] ?? '';
    // Not an /:id sub-action URL
    if (!rawSuffix || rawSuffix.startsWith('?')) {
      const params = new URLSearchParams(url.split('?')[1] ?? '');
      let result = [...MOCK_PLANS];
      const category = params.get('category');
      const status   = params.get('status');
      const entity   = params.get('legalEntityId');
      if (category)      result = result.filter(p => p.category === category);
      if (status)        result = result.filter(p => p.status   === status);
      if (entity)        result = result.filter(p => p.legalEntityIds.includes(entity));
      return ok(result);
    }
  }

  // GET /benefit/plans/:id
  if (req.method === 'GET' && /\/benefit\/plans\/[^/]+$/.test(url)) {
    const id   = url.split('/benefit/plans/')[1];
    const plan = MOCK_PLANS.find(p => p.id === id);
    return ok(plan ?? null);
  }

  // POST /benefit/plans
  if (req.method === 'POST' && url.endsWith('/benefit/plans')) {
    const body = req.body as Partial<BenefitPlan>;
    const created: BenefitPlan = {
      id: nextPlanId(),
      enrolledCount: 0,
      createdAt: new Date().toISOString(),
      ...body
    } as BenefitPlan;
    MOCK_PLANS = [created, ...MOCK_PLANS];
    return ok(created);
  }

  // PUT /benefit/plans/:id/publish
  if (req.method === 'PUT' && /\/benefit\/plans\/[^/]+\/publish$/.test(url)) {
    const id = url.split('/benefit/plans/')[1].replace('/publish', '');
    MOCK_PLANS = MOCK_PLANS.map(p => p.id === id ? { ...p, status: 'ACTIVE' as const } : p);
    return ok(MOCK_PLANS.find(p => p.id === id));
  }

  // PUT /benefit/plans/:id/renew
  if (req.method === 'PUT' && /\/benefit\/plans\/[^/]+\/renew$/.test(url)) {
    const id      = url.split('/benefit/plans/')[1].replace('/renew', '');
    const payload = req.body as { validityStart: string; validityEnd: string | null };
    MOCK_PLANS = MOCK_PLANS.map(p =>
      p.id === id
        ? { ...p, status: 'ACTIVE' as const, validityStart: payload.validityStart, validityEnd: payload.validityEnd }
        : p
    );
    return ok(MOCK_PLANS.find(p => p.id === id));
  }

  // PUT /benefit/plans/:id
  if (req.method === 'PUT' && /\/benefit\/plans\/[^/]+$/.test(url)) {
    const id      = url.split('/benefit/plans/')[1];
    const payload = req.body as Partial<BenefitPlan>;
    MOCK_PLANS = MOCK_PLANS.map(p => p.id === id ? { ...p, ...payload, updatedAt: new Date().toISOString() } : p);
    return ok(MOCK_PLANS.find(p => p.id === id));
  }

  // ── Enrolments ─────────────────────────────────────────────────────────────

  if (req.method === 'GET' && url.includes('/benefit/enrolments/my/summary')) {
    return ok(MOCK_SUMMARY);
  }

  if (req.method === 'GET' && url.includes('/benefit/enrolments/my')) {
    return ok(MOCK_ENROLMENTS.filter(e => e.employeeId === 'emp-123'));
  }

  if (req.method === 'GET' && url.includes('/benefit/enrolments')) {
    const params  = new URLSearchParams(url.split('?')[1] ?? '');
    const planId  = params.get('planId');
    const result  = planId ? MOCK_ENROLMENTS.filter(e => e.planId === planId) : MOCK_ENROLMENTS;
    return ok(result);
  }

  // POST /benefit/enrolments/check-conflicts
  if (req.method === 'POST' && url.endsWith('/benefit/enrolments/check-conflicts')) {
    const body    = req.body as { planId: string; employeeIds: string[] };
    const plan    = MOCK_PLANS.find(p => p.id === body.planId);
    if (!plan) return ok([]);
    const result: EnrolmentConflict[] = [];
    for (const empId of body.employeeIds) {
      const existing = MOCK_ENROLMENTS.find(
        e => e.employeeId === empId && e.subType === plan.subType && e.status === 'ACTIVE' && e.planId !== plan.id
      );
      if (existing) {
        const emp = MOCK_ALL_ELIGIBLE.find(e => e.id === empId);
        result.push({
          employeeId:       empId,
          employeeName:     emp?.name ?? empId,
          existingPlanId:   existing.planId,
          existingPlanName: existing.planName,
          planId:           plan.id,
          planName:         plan.name
        });
      }
    }
    return ok(result);
  }

  // POST /benefit/enrolments/bulk
  if (req.method === 'POST' && url.includes('/benefit/enrolments/bulk')) {
    const body      = req.body as { planId: string; employeeIds?: string[]; conflictResolutions: Array<{ employeeId: string; action: 'SWITCH' | 'SKIP' }> };
    const skipIds   = new Set((body.conflictResolutions ?? []).filter(r => r.action === 'SKIP').map(r => r.employeeId));
    const switchIds = new Set((body.conflictResolutions ?? []).filter(r => r.action === 'SWITCH').map(r => r.employeeId));
    const toEnrol   = (body.employeeIds ?? []).filter(id => !skipIds.has(id));
    return ok({ enrolled: toEnrol.length - switchIds.size, switched: switchIds.size, skipped: skipIds.size });
  }

  // PUT /benefit/enrolments/:id/cancel
  if (req.method === 'PUT' && /\/benefit\/enrolments\/[^/]+\/cancel$/.test(url)) {
    const id = url.split('/benefit/enrolments/')[1].replace('/cancel', '');
    MOCK_ENROLMENTS = MOCK_ENROLMENTS.map(e =>
      e.id === id ? { ...e, status: 'CANCELLED' as const } : e
    );
    return ok(MOCK_ENROLMENTS.find(e => e.id === id));
  }

  // ── Claims ────────────────────────────────────────────────────────────────

  if (req.method === 'GET' && url.includes('/benefit/claims/my')) {
    return ok(MOCK_CLAIMS.filter(c => c.employeeId === 'emp-123'));
  }

  if (req.method === 'GET' && url.includes('/benefit/claims')) {
    const params = new URLSearchParams(url.split('?')[1] ?? '');
    const status = params.get('status');
    const result = status ? MOCK_CLAIMS.filter(c => c.status === status) : MOCK_CLAIMS;
    return ok(result);
  }

  if (req.method === 'POST' && url.endsWith('/benefit/claims')) {
    const body = req.body as Partial<BenefitClaim>;
    const plan = MOCK_PLANS.find(p => p.id === body.planId);
    const initialStatus: BenefitClaim['status'] = plan?.approvalWorkflow === 'AUTO' ? 'APPROVED' : 'PENDING_MANAGER';
    const created: BenefitClaim = {
      id: nextClaimId(),
      employeeId: 'emp-123',
      employeeName: 'Dilshan Rajapaksha',
      employeeDepartment: 'Engineering',
      documentCount: body.documents?.length ?? 0,
      submittedAt: new Date().toISOString(),
      ...body,
      status: initialStatus
    } as BenefitClaim;
    MOCK_CLAIMS = [created, ...MOCK_CLAIMS];
    return ok(created);
  }

  if (req.method === 'PUT' && /\/benefit\/claims\/[^/]+\/approve$/.test(url)) {
    const id = url.split('/benefit/claims/')[1].replace('/approve', '');
    MOCK_CLAIMS = MOCK_CLAIMS.map(c => c.id === id ? { ...c, status: 'APPROVED' as const } : c);
    return ok(MOCK_CLAIMS.find(c => c.id === id));
  }

  if (req.method === 'PUT' && /\/benefit\/claims\/[^/]+\/reject$/.test(url)) {
    const id      = url.split('/benefit/claims/')[1].replace('/reject', '');
    const payload = req.body as { reason: string };
    MOCK_CLAIMS = MOCK_CLAIMS.map(c =>
      c.id === id ? { ...c, status: 'REJECTED' as const, approverNote: payload.reason } : c
    );
    return ok(MOCK_CLAIMS.find(c => c.id === id));
  }

  // PUT /benefit/claims/:id/cancel
  if (req.method === 'PUT' && /\/benefit\/claims\/[^/]+\/cancel$/.test(url)) {
    const id = url.split('/benefit/claims/')[1].replace('/cancel', '');
    MOCK_CLAIMS = MOCK_CLAIMS.map(c => c.id === id ? { ...c, status: 'CANCELLED' as const } : c);
    return ok(MOCK_CLAIMS.find(c => c.id === id));
  }

  // PUT /benefit/claims/:id
  if (req.method === 'PUT' && /\/benefit\/claims\/[^/]+$/.test(url)) {
    const id      = url.split('/benefit/claims/')[1];
    const payload = req.body as Partial<BenefitClaim>;
    MOCK_CLAIMS = MOCK_CLAIMS.map(c =>
      c.id === id ? { ...c, ...payload, documentCount: payload.documents?.length ?? c.documentCount } : c
    );
    return ok(MOCK_CLAIMS.find(c => c.id === id));
  }

  // ── Dependents ────────────────────────────────────────────────────────────

  if (req.method === 'GET' && url.includes('/benefit/dependents/my')) {
    return ok(MOCK_DEPENDENTS);
  }

  if (req.method === 'POST' && url.endsWith('/benefit/dependents')) {
    const body    = req.body as Partial<BenefitDependent>;
    const created: BenefitDependent = {
      id: nextDependentId(),
      employeeId: 'emp-123',
      verificationStatus: 'PENDING',
      coveredPlanIds: [],
      createdAt: new Date().toISOString(),
      submittedDocuments: [],
      ...body
    } as BenefitDependent;
    MOCK_DEPENDENTS = [created, ...MOCK_DEPENDENTS];
    return ok(created);
  }

  // PUT /benefit/dependents/:id
  if (req.method === 'PUT' && /\/benefit\/dependents\/[^/]+$/.test(url)) {
    const id      = url.split('/benefit/dependents/')[1];
    const payload = req.body as Partial<BenefitDependent>;
    MOCK_DEPENDENTS = MOCK_DEPENDENTS.map(d => d.id === id ? { ...d, ...payload } : d);
    return ok(MOCK_DEPENDENTS.find(d => d.id === id));
  }

  return next(req);
};
